/** Help content: the glossary and the runbook index, decoded fail-closed from the bundled JSON (U099). */
import {
  ContractViolation,
  exact,
  field,
  listOf,
  str,
  type Reader,
} from "../../contracts/decode.ts";
import glossaryText from "../../content/glossary.json?raw";
import runbooksText from "../../content/runbooks.json?raw";

export interface GlossaryEntry {
  readonly term: string;
  readonly definition: string;
}

export interface RunbookEntry {
  readonly file: string;
  readonly title: string;
}

const FILE = /^[a-z0-9-]+\.md$/i;
/** A host name or URL in content would be a link target; the runbook index carries none. */
const HOSTLIKE = /:\/\/|\bwww\.|\.(com|org|net|io|in|dev)\b/i;

/** A contact (an e-mail address or a phone number) never belongs in bundled help (review F14). */
const CONTACT = /[^\s@]+@[^\s@]+|\+?\d[\d ()-]{7,}\d/;

const glossaryEntry: Reader<GlossaryEntry> = (value, path) => {
  const found = exact(value, path, ["term", "definition"]);
  const entry = {
    term: field(found, path, "term", str),
    definition: field(found, path, "definition", str),
  };
  const words = `${entry.term} ${entry.definition}`;
  if (HOSTLIKE.test(words) || CONTACT.test(words)) {
    throw new ContractViolation(path, "a glossary entry without a host, URL or contact");
  }
  return entry;
};

const runbookEntry: Reader<RunbookEntry> = (value, path) => {
  const found = exact(value, path, ["file", "title"]);
  const entry = { file: field(found, path, "file", str), title: field(found, path, "title", str) };
  if (!FILE.test(entry.file) || HOSTLIKE.test(entry.title)) {
    throw new ContractViolation(path, "a runbook file name and a title without a host or URL");
  }
  return entry;
};

/** Decode the glossary JSON; terms must be unique. */
export function decodeGlossary(text: string): readonly GlossaryEntry[] {
  const entries = listOf(glossaryEntry)(JSON.parse(text) as unknown, "glossary");
  if (new Set(entries.map((entry) => entry.term.toLowerCase())).size !== entries.length) {
    throw new ContractViolation("glossary", "unique terms");
  }
  return entries;
}

/** Decode the runbook index JSON. */
export function decodeRunbooks(text: string): readonly RunbookEntry[] {
  return listOf(runbookEntry)(JSON.parse(text) as unknown, "runbooks");
}

export const GLOSSARY: readonly GlossaryEntry[] = decodeGlossary(glossaryText);
export const RUNBOOKS: readonly RunbookEntry[] = decodeRunbooks(runbooksText);

/** The entries whose term or definition contains `query` (case-insensitive); an empty query keeps all. */
export function filterGlossary(
  entries: readonly GlossaryEntry[],
  query: string,
): readonly GlossaryEntry[] {
  const needle = query.trim().toLowerCase();
  return needle === ""
    ? entries
    : entries.filter(
        (entry) =>
          entry.term.toLowerCase().includes(needle) ||
          entry.definition.toLowerCase().includes(needle),
      );
}
